import strings from '../../strings/strings.json';
import useGetRequest from '../../hooks/useGetRequest';
import { NotificationModel } from '../../models/Notification';
import MainSection from '../../components/sections/MainSection';
import { sizes } from '../../theme';
import useModifyRequest from '../../hooks/useModifyRequest';
import React from 'react';
import Notification, { NotificationType } from '../../components/notifiers/Notification';
import styled from 'styled-components';
import Button from 'components/buttons/Button';
import { ErrorNotifier } from 'components/notifiers/ErrorNotifier';
import { Notifications } from 'components/notifications/Notifications';
import { useNavigate } from 'react-router-dom';
import Header from 'components/sections/Header';
import NavBackButton from 'components/buttons/NavBackButton';
import { AuthContext } from 'contextProviders/AuthContext';
import { RoleType } from 'models/User';

export default function NotificationsPage(): JSX.Element {
    const { userData } = React.useContext(AuthContext);

    const {
        data: notifications,
        loading: loadingNotifications,
        errors,
        refetchData: refetchNotifications,
    } = useGetRequest<NotificationModel[]>('users/me/notifications');

    const { modifyData: acknowledgeAllNotifications, loading: acknowledgeAllNotificationsLoading } = useModifyRequest(
        `users/me/notifications/acknowledge/all`,
        'POST',
    );

    const navigate = useNavigate();

    const [showMarkAllAsReadError, setShowMarkAllAsReadError] = React.useState<boolean>(false);

    const showMarkAllNotificationsAsRead = notifications && notifications.some((n) => !n.acknowledged);

    async function markAllNotificationsAsRead() {
        setShowMarkAllAsReadError(false);

        const response = await acknowledgeAllNotifications({});
        if (!!response && !!response.value && response.value === true) {
            await refetchNotifications();
        } else {
            setShowMarkAllAsReadError(true);
        }
    }

    let backButton: JSX.Element;

    switch (userData.activeRole) {
        case RoleType.LEARNER:
            backButton = (
                <NavBackButton
                    backButtonTitle={strings.learnerCoursePage.backToCoursesButton.title}
                    backButtonAria={strings.learnerCoursePage.backToCoursesButton.aria}
                    backButtonHref="/learner/courses"
                />
            );
            break;
        case RoleType.TRAINER:
            backButton = (
                <NavBackButton
                    backButtonTitle={strings.OACoursePage.backToDashboardButton.title}
                    backButtonAria={strings.OACoursePage.backToDashboardButton.aria}
                    backButtonHref="/trainer/dashboard"
                />
            );
            break;
        case RoleType.ORGANISATION_ADMIN:
            backButton = (
                <NavBackButton
                    backButtonTitle={strings.OACoursePage.backToDashboardButton.title}
                    backButtonAria={strings.OACoursePage.backToDashboardButton.aria}
                    backButtonHref="/organisation/dashboard"
                />
            );
            break;
        default:
            backButton = (
                <NavBackButton
                    backButtonTitle={strings.adminDashboardPage.backButton.title}
                    backButtonAria={strings.adminDashboardPage.backButton.title}
                    backButtonHref="/"
                />
            );
            break;
    }

    const notificationHeader = <Header title={strings.notificationsPage.title} aboveTitleElement={backButton} />;

    if (loadingNotifications) {
        return (
            <>
                {notificationHeader}
                <MainSectionWithPadding></MainSectionWithPadding>
            </>
        );
    }

    if (!!errors && errors.length > 0) {
        return (
            <>
                {notificationHeader}
                <MainSectionWithPadding>
                    <Notification
                        notificationType={NotificationType.DANGER}
                        title={strings.notificationsPage.fetchError}
                    />
                </MainSectionWithPadding>
            </>
        );
    }

    if ((notifications ?? []).length === 0) {
        return (
            <>
                {notificationHeader}
                <MainSectionWithPadding>
                    <Notification
                        notificationType={NotificationType.INFO}
                        title={strings.notificationsPage.noNotificationsYet}
                        maxWidth={'100%'}
                    />
                </MainSectionWithPadding>
            </>
        );
    }

    return (
        <>
            {notificationHeader}
            <MainSectionWithPadding>
                {showMarkAllAsReadError && (
                    <ErrorNotifierContainer>
                        <ErrorNotifier
                            title={strings.notificationsPage.markAsReadError.title}
                            description={strings.notificationsPage.markAsReadError.description}
                        />
                    </ErrorNotifierContainer>
                )}

                <MarkAllAsReadRow>
                    {showMarkAllNotificationsAsRead && (
                        <Button
                            title={strings.notificationsPage.markAsReadButton.title}
                            aria={strings.notificationsPage.markAsReadButton.aria}
                            onClick={async () => await markAllNotificationsAsRead()}
                            disabled={loadingNotifications || acknowledgeAllNotificationsLoading}
                        />
                    )}
                </MarkAllAsReadRow>

                <Notifications
                    notifications={notifications ?? []}
                    disableButtons={loadingNotifications}
                    navigate={navigate}
                />
            </MainSectionWithPadding>
        </>
    );
}

const MainSectionWithPadding = styled(MainSection)`
    padding-top: ${sizes.spacingMd};
`;

const MarkAllAsReadRow = styled.div`
    display: flex;
    justify-content: end;
    margin-bottom: ${sizes.spacingMd};
    min-height: 48px;
`;

const ErrorNotifierContainer = styled.div`
    display: flex;
    margin-top: ${sizes.spacingMd};
`;
